import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHotel, faIdCard } from "@fortawesome/free-solid-svg-icons";
import "./RegistrationCardPage.css";

const API = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

function fmt(d?: string | null) {
  if (!d) return "—";
  const ascii = d.replace(/[٠-٩]/g, c => String("٠١٢٣٤٥٦٧٨٩".indexOf(c)));
  const normalized = ascii.replace(/\//g, "-");
  const parsed = new Date(normalized);
  if (isNaN(parsed.getTime())) return d;
  return parsed.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
}

function resolveImageSrc(s?: string | null): string | null {
  if (!s) return null;

  let filename: string;

  if (s.startsWith("data:")) return s;

  if (s.startsWith("http://") || s.startsWith("https://")) {
    // Extract filename from Supabase Storage URL
    // Pattern: .../object/sign/passport-images/FILENAME.jpg?token=...
    //      or: .../object/public/passport-images/FILENAME.jpg
    const match = s.match(/passport-images\/([^?#]+)/);
    if (match) {
      filename = match[1]; // e.g. "1531358045034505.jpg"
    } else {
      return s; // unknown URL format — use as-is
    }
  } else {
    filename = s; // already just a filename
  }

  const token = localStorage.getItem("ai2_token") || "";
  return `${API}/api/passport-image/${encodeURIComponent(filename)}?token=${encodeURIComponent(token)}`;
}

function PassportImage({ src, label }: { src: string | null; label: string }) {
  const [failed, setFailed] = useState(false);
  return (
    <div className="rc-id-img-wrap">
      {src && !failed ? (
        <img
          src={src}
          className="rc-id-image"
          alt={`ID ${label}`}
          onError={() => setFailed(true)}
        />
      ) : (
        <div className="rc-id-placeholder-img">
          <FontAwesomeIcon icon={faIdCard} style={{ fontSize: 28, color: "rgba(242,235,217,0.3)" }} />
          <span>{src ? "Failed to load" : "Not submitted"}</span>
        </div>
      )}
      <div className="rc-id-label">{label}</div>
    </div>
  );
}

export default function RegistrationCardPage() {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();

  const [booking,   setBooking]   = useState<any>(null);
  const [passports, setPassports] = useState<any[]>([]);
  const [hotel,     setHotel]     = useState<any>(null);
  const [roomMap,   setRoomMap]   = useState<Record<string, any>>({});
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState("");

  const token   = localStorage.getItem("ai2_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!bookingId) return;
    Promise.all([
      axios.get(`${API}/api/bookings/${bookingId}`, { headers }),
      axios.get(`${API}/api/pre-checkin/passport/${bookingId}`, { headers }).catch(() => ({ data: null })),
      axios.get(`${API}/api/hotel/settings`, { headers }).catch(() => ({ data: {} })),
      axios.get(`${API}/api/rooms`, { headers }).catch(() => ({ data: [] })),
    ])
      .then(([bRes, pRes, hRes, rRes]) => {
        setBooking(bRes.data);

        const pData = pRes.data;
        if (Array.isArray(pData) && pData.length > 0) setPassports(pData);
        else if (pData && !Array.isArray(pData)) setPassports([pData]);
        else setPassports([]);

        setHotel(hRes.data);

        const map: Record<string, any> = {};
        (rRes.data || []).forEach((r: any) => {
          map[r.roomid ?? r.roomId] = r;
        });
        setRoomMap(map);
      })
      .catch(e => setError(e?.response?.data?.message ?? e.message ?? "Failed to load"))
      .finally(() => setLoading(false));
  }, [bookingId]);

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/dashboard/manager/checkinout");
  };

  if (loading) return (
    <div className="rc-page">
      <div style={{ color: "rgba(242,235,217,0.4)", marginTop: 80 }}>Loading registration card…</div>
    </div>
  );
  if (error || !booking) return (
    <div className="rc-page">
      <div style={{ color: "#dc2626", marginTop: 80 }}>{error || "Booking not found"}</div>
    </div>
  );

  const guestName    = booking.guestname || booking.guestName || "—";
  const roomId       = booking.roomid    || booking.roomId    || "";
  const room         = roomMap[roomId] || {};
  const roomDisplay  = room.roomnumber || room.roomNumber || roomId;
  const roomType     = room.roomtype   || room.roomType   || "";
  const checkin      = booking.checkin  || booking.checkIn  || null;
  const checkout     = booking.checkout || booking.checkOut || null;
  const nights       = booking.nights   ?? 1;
  const total        = Number(booking.total || 0);

  const hotelName    = hotel?.hotel_name   || hotel?.name   || "Hotel";
  const hotelAddress = hotel?.address      || "";
  const hotelPhone   = hotel?.phone_number || hotel?.phone  || "";
  const hotelLogo    = hotel?.logo_url     || "";
  const checkInTime  = hotel?.check_in     || "14:00";
  const checkOutTime = hotel?.check_out    || "12:00";

  return (
    <div className="rc-page">
      <div className="rc-top-bar">
        <button className="rc-btn rc-btn-back" onClick={handleBack}>Back</button>
        <button className="rc-btn rc-btn-print" onClick={() => window.print()}>Print RC</button>
      </div>

      <div className="rc-card">
        {/* Hotel header */}
        <div className="rc-hotel-header">
          {hotelLogo ? (
            <img src={hotelLogo} alt="Logo" className="rc-hotel-logo" />
          ) : (
            <div className="rc-hotel-logo-placeholder">
              <FontAwesomeIcon icon={faHotel} />
            </div>
          )}
          <div>
            <div className="rc-hotel-name">{hotelName}</div>
            <div className="rc-hotel-address">
              {hotelAddress}{hotelPhone ? ` · ${hotelPhone}` : ""}
            </div>
          </div>
        </div>

        {/* Title bar */}
        <div className="rc-title-bar">
          <div className="rc-title">Registration Card</div>
          <div className="rc-booking-ref">Ref: <strong>{bookingId}</strong></div>
        </div>

        <div className="rc-body">
          {/* Room strip */}
          <div className="rc-room-strip">
            <div>
              <div className="rc-room-item-label">Room</div>
              <div className="rc-room-item-value">{roomDisplay}</div>
              {roomType && <div className="rc-room-item-sub">{roomType}</div>}
            </div>
            <div>
              <div className="rc-room-item-label">Check-in</div>
              <div className="rc-room-item-value" style={{ fontSize: 13 }}>{fmt(checkin)}</div>
              <div className="rc-room-item-sub">{checkInTime}</div>
            </div>
            <div>
              <div className="rc-room-item-label">Check-out</div>
              <div className="rc-room-item-value" style={{ fontSize: 13 }}>{fmt(checkout)}</div>
              <div className="rc-room-item-sub">{checkOutTime}</div>
            </div>
            <div>
              <div className="rc-room-item-label">Nights</div>
              <div className="rc-room-item-value">{nights}</div>
            </div>
          </div>

          {/* Guests */}
          <div>
            <div className="rc-section-title">Guests</div>

            {passports.length === 0 ? (
              <div className="rc-guest-block">
                <div className="rc-guest-name">{guestName}</div>
                <div className="rc-id-placeholder-box">
                  <FontAwesomeIcon icon={faIdCard} style={{ fontSize: 22, color: "rgba(242,235,217,0.3)" }} />
                  <span>No ID submitted</span>
                </div>
              </div>
            ) : (
              passports.map((p, idx) => {
                const name = p.full_name || `${p.first_name || ""} ${p.last_name || ""}`.trim() || p.guest_name || guestName;
                const frontSrc = resolveImageSrc(p.image_front);
                const backSrc  = (p.doc_type === "National ID" || p.doc_type === "ID") ? resolveImageSrc(p.image_back) : null;
                const hasImages = frontSrc || backSrc;

                const isNationalId = p.doc_type === "National ID" || p.doc_type === "ID";

                return (
                  <div key={p.id ?? idx} className="rc-guest-block">
                    <div className="rc-guest-name">
                      Guest {idx + 1}{idx === 0 ? " — Lead Guest" : ""}{name ? ` — ${name}` : ""}
                    </div>
                    <div className="rc-id-images-row">
                      <PassportImage src={frontSrc} label={isNationalId ? "Front" : "Passport"} />
                      {isNationalId && <PassportImage src={backSrc} label="Back" />}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Charges */}
          <div>
            <div className="rc-section-title">Charges</div>
            <div className="rc-grid">
              <div className="rc-field">
                <span className="rc-field-label">Room Rate (Total)</span>
                <span className="rc-field-value">EGP {total.toLocaleString()}</span>
              </div>
              <div className="rc-field">
                <span className="rc-field-label">Payment Method</span>
                <span className="rc-field-value empty">_______________</span>
              </div>
            </div>
          </div>

          {/* Policies */}
          <div className="rc-policies">
            <strong>Hotel Policies:</strong> Check-in time is {checkInTime} · Check-out time is {checkOutTime} ·
            By signing below, I agree to abide by all hotel policies, accept responsibility for any
            damages to hotel property, and authorize the hotel to charge my account for all services
            rendered during my stay.
          </div>

          {/* Signatures */}
          <div className="rc-signature-row">
            <div className="rc-sig-box">Guest Signature &amp; Date</div>
            <div className="rc-sig-box">Front Desk Signature &amp; Date</div>
          </div>
        </div>

        <div className="rc-footer">
          {hotelName} — Thank you for your stay
        </div>
      </div>
    </div>
  );
}
